<template>
  <form
    id="tbe-contact-details-form"
    class="tbe-contact-details-form"
    data-cy="rs-tbe-contact-details-form"
  >
    <div
      class="tbe-contact-details-form__group tbe-contact-details-form__group--first-name"
      :class="{
        'tbe-contact-details-form__group--error': v.firstName.$errors.length
      }"
    >
      <label
        for="firstName"
        class="tbe-contact-details-form__label"
        v-if="!displaySettings.is_widget"
      >
        {{ t('Contact.FirstName') }} <span class="req">*</span>
      </label>
      <input
        id="firstName"
        class="tbe-contact-details-form__control"
        name="firstName"
        :value="customer.firstName"
        @input="handleInputField"
        @change="handleInputField($event, true)"
        :placeholder="displaySettings.is_widget ? `${t('Contact.FirstName')} *` : ''"
      />
      <span
        class="tbe-contact-details-form__error"
        v-for="error of v.firstName.$errors"
        :key="error.$uid"
        >{{ error.$message }}</span
      >
    </div>
    <div
      class="tbe-contact-details-form__group tbe-contact-details-form__group--last-name"
      :class="{
        'tbe-contact-details-form__group--error': v.lastName.$errors.length
      }"
    >
      <label
        for="last_name"
        class="tbe-contact-details-form__label"
        v-if="!displaySettings.is_widget"
      >
        {{ t('Contact.LastName') }} <span class="req">*</span>
      </label>
      <input
        id="last_name"
        class="tbe-contact-details-form__control"
        name="lastName"
        :value="customer.lastName"
        @input="handleInputField"
        @change="handleInputField($event, true)"
        :placeholder="displaySettings.is_widget ? `${t('Contact.LastName')} *` : ''"
      />
      <span
        class="tbe-contact-details-form__error"
        v-for="error of v.lastName.$errors"
        :key="error.$uid"
      >
        {{ error.$message }}
      </span>
    </div>
    <div
      class="tbe-contact-details-form__group tbe-contact-details-form__group--email"
      :class="{ 'tbe-contact-details-form__group--error': v.email.$errors.length }"
    >
      <label for="email" class="tbe-contact-details-form__label" v-if="!displaySettings.is_widget">
        {{ t('Contact.Email') }} <span class="req">*</span>
      </label>
      <input
        id="email"
        class="tbe-contact-details-form__control"
        name="email"
        :value="customer.email"
        @input="handleInputField"
        @change="handleInputField($event, true)"
        :placeholder="displaySettings.is_widget ? `${t('Contact.Email')} *` : ''"
      />
      <span
        class="tbe-contact-details-form__error"
        v-for="error of v.email.$errors"
        :key="error.$uid"
      >
        {{ error.$message }}
      </span>
    </div>
    <div
      class="tbe-contact-details-form__group tbe-contact-details-form__group--phone"
      :class="{ 'tbe-contact-details-form__group--error': v.phone.$errors.length }"
    >
      <label class="tbe-contact-details-form__label" v-if="!displaySettings.is_widget">
        {{ t('Contact.Phone') }} <span class="req">*</span>
      </label>
      <tbe-tel-input
        class="tbe-contact-details-form__control tbe-contact-details-form__control--vti"
        ref="telInputRef"
        name="telephone"
        mode="international"
        :modelValue="customer.phone"
        :validCharactersOnly="true"
        :inputOptions="{
          placeholder: t('Contact.PhonePlaceholder'),
          showDialCode: true
        }"
        :dropdownOptions="{
          showDialCodeInList: true,
          showFlags: true
        }"
        :preferredCountries="['cz', 'es', 'de', 'pt', 'ru']"
        :autoDefaultCountry="false"
        @on-input="handleChangePhone"
      />
      <span
        class="tbe-contact-details-form__error"
        v-for="error of v.phone.$errors"
        :key="error.$uid"
      >
        {{ error.$message }}
      </span>
    </div>
    <div class="tbe-contact-details-form__group">
      <label
        for="message"
        class="tbe-contact-details-form__label"
        v-if="!displaySettings.is_widget"
      >
        {{ t('Contact.Comment') }}
      </label>
      <textarea
        id="message"
        class="tbe-contact-details-form__control tbe-contact-details-form__control--textarea"
        :style="{ height: customer.message.length ? '120px' : '50px' }"
        name="message"
        :value="customer.message"
        @input="handleInputField"
        @change="handleInputField($event, true)"
        maxlength="1024"
        :placeholder="displaySettings.is_widget ? t('Contact.Comment') : ''"
      />
      <small class="characters-count">
        {{ customer.message.length }} / 1024
        {{ t('Global.Characters').toLowerCase() }}
      </small>
    </div>

    <div class="tbe-contact-details-form__group">
      <ul class="reservation-disclaimers">
        <li>{{ t('Contact.DisclaimersFirst') }}</li>
        <li>{{ t('Contact.DisclaimersSecond') }}</li>
        <li>{{ t('Contact.DisclaimersThird') }}</li>
      </ul>
    </div>
  </form>
</template>

<script>
  import { ref, computed, onMounted } from 'vue'
  import { useStore } from 'vuex'
  import { useI18n } from 'vue-i18n'
  import axios from 'axios'

  import useVuelidate from '@vuelidate/core'
  import { required, email, minLength } from '@vuelidate/validators'

  import { VueTelInput as tbeTelInput } from 'vue-tel-input'

  const IP_INFO_TOKEN = 'bd876b3212bd23'

  const rules = {
    firstName: { required },
    lastName: { required },
    phone: { required, minLength: minLength(5) },
    email: { required, email }
  }

  export default {
    name: 'tbeContactDetailsForm',

    components: { tbeTelInput },

    props: {
      busy: Boolean
    },

    setup() {
      const store = useStore()
      const { t } = useI18n()

      const telInputRef = ref(null)

      const supplierSettings = computed(() => store.state.app.supplierSettings)
      const displaySettings = computed(() => store.state.app.displaySettings)

      const customer = computed(() => store.state.booking.customer)
      const updateCustomer = (payload) => store.dispatch('booking/updateCustomer', payload)

      const v = useVuelidate(rules, customer)

      const handleChangePhone = (phone, phoneObject) => {
        const countryCode = phoneObject.countryCode || phoneObject.country?.iso2
        const countryCallingCode = phoneObject.countryCallingCode || phoneObject.country?.dialCode
        updateCustomer({ phone: phoneObject.formatted, countryCode, countryCallingCode })
      }

      const handleInputField = (event, withTrim = false) => {
        const value = !withTrim ? event.target.value : event.target.value.trim()
        updateCustomer({ [event.target.name]: value })
      }

      onMounted(() => {
        axios.get('https://ipinfo.io/json?token=' + IP_INFO_TOKEN).then(({ data }) => {
          const { country } = data
          telInputRef.value.choose(country || 'CZ')
        })
      })

      return {
        telInputRef,
        t,
        v,
        customer,
        supplierSettings,
        displaySettings,
        handleChangePhone,
        handleInputField
      }
    }
  }
</script>

<style lang="scss" scoped>
  .tbe-contact-details-form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 15px;

    &__group {
      grid-column: 1 / 3;

      &--first-name {
        grid-column: 1 / 2;
      }

      &--last-name {
        grid-column: 2 / 3;
      }

      &--error {
        .tbe-contact-details-form__label {
          color: #c62828;
        }

        .tbe-contact-details-form__control,
        .vue-tel-input {
          border-width: 2px;
          border-color: #c62828;
        }

        .tbe-contact-details-form__error {
          display: block;
          font-size: 12px;
          color: #c62828;
          margin-top: 5px;
        }
      }

      .characters-count {
        display: flex;
        justify-content: flex-end;
        margin-top: 5px;
        font-size: 12px;
      }
    }

    &__label {
      display: block;
      font-size: 14px;
      font-weight: 400;
      margin-bottom: 5px;

      .req {
        color: #c62828;
      }
    }

    &__control {
      width: 100%;
      height: 40px;
      padding: 0 10px;
      border-radius: 3px;
      border: 1px solid #bfbfbf;
      background-color: #fff;
      font-size: 14px;
      line-height: 1.6;
      color: #333;

      &:focus {
        outline: none;
        box-shadow: 0 0 0 3px #{var(--rs-accent-color)}30;
      }

      &::placeholder {
        color: #b3b3b3;
      }

      &--textarea {
        resize: vertical;
        height: 120px;
        padding-top: 5px;
        padding-bottom: 5px;
      }

      &--vti {
        height: 40px;
        border: 1px solid #bfbfbf;
        font-size: 14px;
        background-color: #fff;
        line-height: 1.6;
        color: #333;
        padding-left: 0;

        .vti {
          &__input {
            font-size: 14px;
            height: auto;
            &::placeholder {
              font-size: 14px;
            }
          }
        }
      }
    }

    .reservation-disclaimers {
      list-style-type: decimal;
      font-size: 12px;
      margin-left: 20px;
      margin-top: -15px;
      margin-bottom: 15px;

      li {
        margin: 0 0 5px;
        line-height: 1.4;
      }
    }
  }
</style>
